<script setup lang="ts">
import { AanvragenDomainEntitiesOntvangerDirectePost as OntvangerDirectePost } from '@services/aanvragen-public/orval-api/model/aanvragenDomainEntitiesOntvangerDirectePost';
import { type AanvragenMuterenStartMutatieMutatieDto as StartMutatieDto } from '@services/aanvragen-public/orval-api/model/aanvragenMuterenStartMutatieMutatieDto';
import { useMuterenStart } from '@services/aanvragen-public/orval-api/muteren-api/muteren-api';
import VModal from '@shared/component/VModal/VModal.vue';
import { toDateonly } from '@tools/dates';
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { useField, useForm } from 'vee-validate';
import CheckboxField from '@shared/form/CheckboxField/CheckboxField.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import TextField from '@shared/form/TextField/TextField.vue';
import VDatePicker from '@shared/form/VDatePicker/VDatePicker.vue';
import FieldRowGroup from '@shared/form/FieldRowV2/FieldRowGroup.vue';
import { useErrorHandler } from '@errors/useErrorHandler';
import { AanvragenDomainEntitiesMuterenMutatieActie as MutatieActie } from '@services/aanvragen-public/orval-api/model/aanvragenDomainEntitiesMuterenMutatieActie';
import { usePakketpolisStore } from '../pakketpolisStore';
import { type MutatieGestart } from '../PakketpolisToolbar.vue';

const props = defineProps<{
  pakketpolisnummer: string;
  titel?: string;
  isWagenpark: boolean;
  relatienummer: number;
}>();

const emit = defineEmits<{
  mutatieGestart: [MutatieGestart];
}>();

const { handleError } = useErrorHandler();

const store = usePakketpolisStore();

const isOpen = defineModel<boolean>('isOpen');
const mutatiedatum = ref<Date>(new Date());
const latenUitvallen = ref<boolean>(true);
const extraOpmerkingenField = useTemplateRef('extraOpmerkingenField');

const pakketpolis = computed(() => store.getPakketpolis(props.pakketpolisnummer));
const extraOpmerkingenLabel = computed(() =>
  props.isWagenpark ? 'Mutatiereden' : 'Extra opmerking',
);

const validationSchema = computed(() =>
  toTypedSchema(
    z.object({
      extraOpmerkingen: z.string().min(1, `${extraOpmerkingenLabel.value} is verplicht.`),
    }),
  ),
);

const { errors, handleSubmit, isSubmitting } = useForm({
  validationSchema,
});

const { value: extraOpmerkingen } = useField<string>('extraOpmerkingen');

const { mutateAsync: startMutatie } = useMuterenStart({
  request: {
    timeout: 2 * 60 * 1000,
  },
});

const onSubmit = handleSubmit(async () => {
  if (!pakketpolis.value?.mutatieActies.dummymutatieStarten?.isMogelijk) return;

  const dto: StartMutatieDto = {
    isDummy: true,
    mutatiedatum: toDateonly(mutatiedatum.value),
    forceerUitval: latenUitvallen.value,
    hoofdvervaldatum: pakketpolis.value.originelePakketpolis.hoofdvervaldatum,
    incassocode: pakketpolis.value.originelePakketpolis.incasso.code,
    termijn: pakketpolis.value.originelePakketpolis.termijn.termijn,
    nieuweProducten: [],
    teMuterenPolissen: constructTeMuterenPolissen(),
    ontvangerDirectePost: OntvangerDirectePost.Adviseur,
    extraOpmerkingen: extraOpmerkingen.value,
    isBeursPakket: false,
    relatienummer: props.relatienummer,
    contractnummer: props.isWagenpark
      ? pakketpolis.value.originelePakketpolis.contractnummer
      : undefined,
  };
  try {
    const res = await startMutatie({ polisnummerpakket: props.pakketpolisnummer, data: dto });
    const event: MutatieGestart = {
      directTeVerwerken: res.directTeVerwerken,
      offertenummer: res.offertenummer,
      offerteproductId: res.offerteproductId,
      isBeursPakket: false,
      wagenparkId: res.wagenparkId,
    };
    emit('mutatieGestart', event);
  } catch (e: any) {
    handleError(e);
  }
});

function constructTeMuterenPolissen() {
  return props.isWagenpark
    ? // De backend matcht de ene Wagenpark-subpolis op een leeg polisnummer.
      [
        {
          polisnummer: '',
          actie: MutatieActie.Muteren,
          mutatiedatum: toDateonly(mutatiedatum.value),
          mutatiereden: extraOpmerkingen.value,
        },
      ]
    : [];
}

watch(isOpen, () => {
  if (isOpen.value) {
    nextTick(() => extraOpmerkingenField.value?.focus());
  }
});

watch(
  () => pakketpolis.value?.extraOpmerkingen,
  (eo) => {
    if (eo) {
      extraOpmerkingen.value = eo ?? '';
    }
  },
  { immediate: true },
);

function close() {
  isOpen.value = false;
}

function toggleLatenUitvallen() {
  latenUitvallen.value = !latenUitvallen.value;
}
</script>

<template>
  <VModal
    v-if="pakketpolis"
    :is-active="isOpen"
    @close-modal="close"
    @confirm-action="onSubmit"
    :title="props.titel ?? 'Dummy mutatie starten'"
    :confirm-button-text="props.titel ?? 'Dummy mutatie starten'"
    :overflow="true"
    :invert-header="true"
    :loading="isSubmitting"
  >
    <FieldRowGroup :gap="2">
      <FieldRowV2
        class="mb-2"
        label="Mutatiedatum"
        :tooltip="pakketpolis.mutatieActies.dummymutatieStarten?.melding ?? undefined"
        :label-on-own-row="true"
        label-text-color="primary900"
      >
        <VDatePicker
          v-model="mutatiedatum"
          :disabled="!pakketpolis.mutatieActies.dummymutatieStarten?.isMogelijk"
          :clearable="false"
          data-testid="dummy-ingangsdatum"
        />
      </FieldRowV2>
      <FieldRowV2
        :label="extraOpmerkingenLabel"
        :label-on-own-row="true"
        label-text-color="primary900"
        :errors="[errors.extraOpmerkingen]"
      >
        <TextField
          v-model:value="extraOpmerkingen"
          :rows="4"
          :placeholder="
            isWagenpark
              ? `In het volgende scherm kun je voertuigen toevoegen, royeren, of wijzigen. Geef hier aan wat de reden van de mutatie is en of je nog andere opmerkingen hebt.`
              : `Vul hier extra opmerkingen in`
          "
          ref="extraOpmerkingenField"
        />
      </FieldRowV2>
      <FieldRowV2 v-if="!isWagenpark">
        <CheckboxField
          path="latenUitvallen"
          v-model="latenUitvallen"
          sub-label="Mutatie uit laten vallen"
          @click="toggleLatenUitvallen"
        />
      </FieldRowV2>
    </FieldRowGroup>
  </VModal>
</template>
